import { compact, debounce, isEmpty } from 'lodash'
import { ReactElement, useMemo } from 'react'
import AssignedToFilter from '../../table/filters/AssignedToFilter'
import DateRangeFilter from '../../table/filters/DateRangeFilter'
import FilterConfig from '../../table/filters/FilterConfig'
import { FilterType, emptyFilter } from './FiltersContainer'
import { FilterObject, FilterOperator, FilterValueType, FilterableColumn, getReadableOperator } from 'app/filter.utils'
import { cn } from 'app/lib/utils'
import { OptionType } from 'types/OptionType'
import { Input } from '../../ui/input'

/**
 * It uses FilterValueType to decide which component to render
 * There are four posible outcomes:
 * - DateRangeFilter: when FilterValueType.rangeDate
 * - AssignedToFilter: when FilterValueType.assignedTo
 * - FilterConfig: when FilterValueType.stringArray, FilterValueType.id, FilterValueType.multiple, FilterValueType.string
 * - input: when FilterValueType.string and no column is selected
 * When no column is selected the input is disabled

 * @Prop index - Index of the term to update
 * @Prop selectedColumn - column selected for the term (if any)
 * @Prop filters - list of user selected filters
 * @Prop onValueChange function to update the filter
 * @returns
 */
export function FilterValue({
  index,
  selectedColumn,
  filters,
  onValueChange,
  open,
}: {
  selectedColumn?: FilterableColumn
  filters?: FilterType[]
  index: number
  onValueChange: (value: FilterType[]) => void
  open?: boolean
}): ReactElement | null {
  let filterObject: FilterObject | undefined
  if (selectedColumn) {
    filterObject = {
      attribute: selectedColumn.attribute,
      displayName: selectedColumn.displayName,
      operator:
        (filters?.[index]?.operator as FilterOperator) ?? selectedColumn.operator ?? selectedColumn.defaultOperator,
      type: selectedColumn.type,
      options: selectedColumn.options,
      value: filters?.[index]?.value ?? selectedColumn.defaultValue,
      defaultValue: selectedColumn.defaultValue,
      uniqOperator: selectedColumn.uniqOperator,
      dateOperator: selectedColumn.dateOperator,
      readOnly: selectedColumn.readOnly,
      limitToMonth: selectedColumn.limitToMonth,
      includeToday: selectedColumn.includeToday,
      includeYesterday: selectedColumn.includeYesterday,
      includeWeeks: selectedColumn.includeWeeks,
      useAsterisk: selectedColumn.useAsterisk,
    }
  }

  let contextualValue: string | string[] | undefined = useMemo(() => {
    if (isEmpty(selectedColumn?.contextualFiltering)) return undefined
    return compact(filters?.find((filter) => selectedColumn?.contextualFiltering?.[filter.attribute!])?.value ?? ``)
  }, [selectedColumn, filters])

  return filterObject && selectedColumn && selectedColumn.type !== FilterValueType.string ? (
    <div className={cn(`h-8`, selectedColumn.dayWindows ? `min-w-60` : `w-60`)}>
      {selectedColumn.type === FilterValueType.rangeDate ? (
        <DateRangeFilter
          filter={filterObject}
          column={selectedColumn}
          onEdit={(val, operator) => {
            let updatedTerms = [...(filters ?? [emptyFilter])]
            updatedTerms.splice(index, 1, { ...filters?.[index], value: val, operator })
            onValueChange(updatedTerms)
          }}
          onRemove={() => {}}
          limitToMonth={selectedColumn.limitToMonth}
          includeToday={selectedColumn.includeToday}
          includeYesterday={selectedColumn.includeYesterday}
          includeWeeks={selectedColumn.includeWeeks}
          includeInThePast={selectedColumn.includeInThePast}
          includeFuture={selectedColumn.includeFuture}
          includeAll={selectedColumn.includeAll}
        />
      ) : selectedColumn.type === FilterValueType.assignedTo ? (
        <AssignedToFilter
          filter={filterObject}
          column={selectedColumn}
          onSelect={(val: OptionType[]) => {
            let updatedTerms = [...(filters ?? [emptyFilter])]
            updatedTerms.splice(index, 1, {
              ...filters?.[index],
              value: val?.map((user) => user.value!).flat(),
              secondaryAttribute: selectedColumn.secondaryAttribute,
              secondaryValue: val.filter((user) => user.id !== `0`).map((user) => user.id) ?? undefined,
              operator: `in`,
              displayOperator: `Includes`,
            })
            onValueChange(updatedTerms)
          }}
        />
      ) : (
        <>
          {selectedColumn.type !== FilterValueType.readOnly ? (
            <FilterConfig
              filter={filterObject}
              column={selectedColumn}
              contextualValue={contextualValue}
              operator={(filters?.[index]?.operator as FilterOperator) ?? filterObject.operator}
              openFilterOptions={open}
              onValueChange={(val) => {
                let updatedTerms = [...(filters ?? [emptyFilter])]
                updatedTerms.splice(index, 1, {
                  ...filters?.[index],
                  value: val,
                  ...(selectedColumn?.defaultFilter && {
                    operator: filters?.[index].operator ?? selectedColumn?.defaultOperator,
                    displayOperator:
                      filters?.[index].displayOperator ??
                      getReadableOperator(selectedColumn?.defaultOperator!, selectedColumn?.type),
                  }),
                })
                onValueChange(updatedTerms)
              }}
            />
          ) : null}
        </>
      )}
    </div>
  ) : (
    <Input
      type="text"
      className="h-8 w-60 rounded-lg border border-default bg-surface px-2 text-body-sm disabled:text-minimal disabled:opacity-70"
      placeholder="Search"
      defaultValue={filters?.[index]?.value === `*` ? `Any` : filters?.[index]?.value}
      disabled={!filters?.[index]?.attribute || selectedColumn?.readOnly}
      onChange={debounce((event) => {
        let updatedTerms = [...(filters ?? [emptyFilter])]
        updatedTerms.splice(index, 1, { ...filters?.[index], value: event.target.value })
        onValueChange(updatedTerms)
      }, 250)}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    />
  )
}
